import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { ACTIONS, ACHIEVEMENTS, SEASONAL_FACTORS } from '../data/actions';

const STORAGE_KEY = "greenplan_v4";

const loadFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const saveToStorage = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
};

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const saved = loadFromStorage();
  
  // Core state
  const [view, setView] = useState("home");
  const [items, setItems] = useState(() => 
    saved?.items || ACTIONS.map(a => ({ ...a, done: false }))
  );
  const [calculatorAnswers, setCalculatorAnswers] = useState(() => saved?.answers || {});
  const [calculatorStep, setCalculatorStep] = useState(0);
  const [calculatorComplete, setCalculatorComplete] = useState(() => !!saved?.calculatorComplete);
  const [footprintHistory, setFootprintHistory] = useState(() => saved?.footprintHistory || []);
  const [expandedAction, setExpandedAction] = useState(null);
  const [filterTier, setFilterTier] = useState("ALL");
  const [filterDifficulty, setFilterDifficulty] = useState("ALL");
  const [userName, setUserName] = useState(() => saved?.userName || "");
  const [userNameEdit, setUserNameEdit] = useState(false);
  const [nameInput, setNameInput] = useState("");
  
  // Region selection (new feature)
  const [region, setRegion] = useState(() => saved?.region || "uk");
  
  // Seasonal adjustment
  const [season, setSeason] = useState(() => saved?.season || "yearRound");
  
  // Goal setting
  const [userGoal, setUserGoal] = useState(() => saved?.userGoal || null);
  const [goalTimeline, setGoalTimeline] = useState(() => saved?.goalTimeline || "1year");
  const [theme, setTheme] = useState(() => (saved?.theme === "light" ? "light" : "dark"));
  
  // Achievements
  const [unlockedAchievements, setUnlockedAchievements] = useState(() => saved?.achievements || []);
  const [showAchievementPopup, setShowAchievementPopup] = useState(null);
  
  // UI state
  const [toast, setToast] = useState(null);
  const [undoState, setUndoState] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(() => 
    saved?.onboardingDone ? null : 0
  );
  
  // Refs
  const undoTimerRef = useRef(null);
  const confettiTimerRef = useRef(null);
  const prevDoneCount = useRef(items.filter(i => i.done).length);

  // Persist state
  useEffect(() => {
    saveToStorage({
      items,
      answers: calculatorAnswers,
      calculatorComplete,
      footprintHistory,
      userName,
      onboardingDone: onboardingStep === null,
      region,
      achievements: unlockedAchievements,
      season,
      userGoal,
      goalTimeline,
      theme,
    });
  }, [items, calculatorAnswers, calculatorComplete, footprintHistory, userName, onboardingStep, region, unlockedAchievements, season, userGoal, goalTimeline, theme]);

  // Computed values
  const doneActions = items.filter(i => i.done);
  const totalCO2Saved = doneActions.reduce((sum, action) => sum + action.co2Saved, 0);
  const progressPercent = Math.round((doneActions.length / items.length) * 100);
  
  // Footprint with seasonal adjustment
  const seasonalFactor = SEASONAL_FACTORS[season] || SEASONAL_FACTORS.yearRound;
  const rawFootprint = Object.values(calculatorAnswers).reduce((sum, val) => sum + val, 0);
  const footprint = rawFootprint * seasonalFactor.energy;
  const footprintLabel = footprint < 5 ? "Low 🌿" : footprint < 9 ? "Average 🟡" : "High 🔴";
  const potentialSavings = items.filter(a => !a.done).reduce((sum, a) => sum + a.co2Saved, 0);
  const highImpactDone = items.filter(a => a.done && a.tier === "HIGH").length;
  
  // Goal-related computed
  const goalReduction = userGoal ? Math.max(0, footprint - userGoal) : 0;
  const goalProgress = userGoal && footprint > userGoal 
    ? Math.min(100, ((footprint - userGoal) / (footprint - userGoal + potentialSavings/1000)) * 100)
    : userGoal && footprint <= userGoal ? 100 : 0;
  const goalTimelineMonths = goalTimeline === "6months" ? 6 : goalTimeline === "1year" ? 12 : 24;
  const monthlyReduction = goalReduction / goalTimelineMonths;

  // Check achievements
  const checkAchievements = useCallback((doneCount, total, co2, highImpact) => {
    const newAchievements = [];
    
    ACHIEVEMENTS.forEach(achievement => {
      if (!unlockedAchievements.includes(achievement.id) && 
          achievement.condition(doneCount, total, co2, highImpact)) {
        newAchievements.push(achievement);
      }
    });
    
    if (newAchievements.length > 0) {
      setUnlockedAchievements(prev => [...prev, ...newAchievements.map(a => a.id)]);
      setShowAchievementPopup(newAchievements[0]);
      setTimeout(() => setShowAchievementPopup(null), 3500);
    }
  }, [unlockedAchievements]);

  // Actions
  const triggerConfetti = useCallback(() => {
    setConfetti(true);
    clearTimeout(confettiTimerRef.current);
    confettiTimerRef.current = setTimeout(() => setConfetti(false), 2200);
  }, []);

  const showToast = useCallback((message, emoji = "✅") => {
    setToast({ message, emoji });
    setTimeout(() => setToast(null), 2600);
  }, []);

  const toggleAction = useCallback((id) => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);

    setItems(prev => {
      const next = prev.map(a => a.id === id ? { ...a, done: !a.done } : a);
      const action = prev.find(a => a.id === id);
      const wasCommitting = !action.done;

      setUndoState({ id, wasCommitting, title: action.title });
      undoTimerRef.current = setTimeout(() => setUndoState(null), 4000);

      const nextDone = next.filter(i => i.done).length;
      const prevCount = prevDoneCount.current;
      prevDoneCount.current = nextDone;

      if (wasCommitting) {
        const newCO2 = next.filter(i => i.done).reduce((s, a) => s + a.co2Saved, 0);
        const newHighImpact = next.filter(a => a.done && a.tier === "HIGH").length;
        checkAchievements(nextDone, next.length, newCO2, newHighImpact);

        if (prevCount === 0 && nextDone === 1) {
          setTimeout(() => triggerConfetti(), 100);
          showToast("First action committed! 🎉", "🌱");
        } else if (prevCount < Math.round(prev.length * 0.5) && nextDone >= Math.round(prev.length * 0.5)) {
          setTimeout(() => triggerConfetti(), 100);
          showToast("Halfway there! Keep going!", "🎊");
        } else if (nextDone === prev.length) {
          setTimeout(() => triggerConfetti(), 100);
          showToast("All actions committed! Amazing!", "🏆");
        } else {
          showToast(`Committed: "${action.title}"`, "🎉");
        }
      }

      return next;
    });
  }, [showToast, triggerConfetti, checkAchievements]);

  const handleUndo = useCallback(() => {
    if (!undoState) return;
    clearTimeout(undoTimerRef.current);
    setItems(prev => prev.map(a => 
      a.id === undoState.id ? { ...a, done: !undoState.wasCommitting } : a
    ));
    setUndoState(null);
    showToast("Action undone", "↩");
  }, [undoState, showToast]);

  const handleCalculatorAnswer = (questionId, value) => {
    const nextAnswers = { ...calculatorAnswers, [questionId]: value };
    setCalculatorAnswers(nextAnswers);
    
    if (calculatorStep < 6) {
      setCalculatorStep(s => s + 1);
    } else {
      const total = Object.values(nextAnswers).reduce((sum, val) => sum + val, 0);
      setCalculatorComplete(true);
      setFootprintHistory(prev => [
        { 
          fp: parseFloat((total * seasonalFactor.energy).toFixed(1)), 
          date: new Date().toLocaleDateString("en-GB", { 
            day: "numeric", 
            month: "short", 
            year: "numeric" 
          }) 
        },
        ...prev
      ].slice(0, 6));
      showToast("Footprint calculated!", "📊");
    }
  };

  const resetCalculator = () => {
    setCalculatorComplete(false);
    setCalculatorStep(0);
    setCalculatorAnswers({});
  };

  const toggleTheme = useCallback(() => {
    setTheme(prev => (prev === "dark" ? "light" : "dark"));
  }, []);

  const exportProgress = () => {
    const data = JSON.stringify({ 
      items, 
      answers: calculatorAnswers, 
      calculatorComplete, 
      footprintHistory, 
      userName,
      region,
      achievements: unlockedAchievements,
      theme,
      exportedAt: new Date().toISOString(),
    }, null, 2);
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `greenplan-progress-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showToast("Progress exported!", "💾");
  };

  const importProgress = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        if (parsed.items) setItems(parsed.items);
        if (parsed.answers) setCalculatorAnswers(parsed.answers);
        if (parsed.calculatorComplete !== undefined) setCalculatorComplete(parsed.calculatorComplete);
        if (parsed.footprintHistory) setFootprintHistory(parsed.footprintHistory);
        if (parsed.userName) setUserName(parsed.userName);
        if (parsed.region) setRegion(parsed.region);
        if (parsed.achievements) setUnlockedAchievements(parsed.achievements);
        if (parsed.theme === "dark" || parsed.theme === "light") setTheme(parsed.theme);
        showToast("Progress imported!", "📂");
      } catch {
        showToast("Invalid file format", "❌");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const value = {
    // State
    view, setView,
    items, setItems,
    calculatorAnswers, setCalculatorAnswers,
    calculatorStep, setCalculatorStep,
    calculatorComplete, setCalculatorComplete,
    footprintHistory, setFootprintHistory,
    expandedAction, setExpandedAction,
    filterTier, setFilterTier,
    filterDifficulty, setFilterDifficulty,
    userName, setUserName,
    userNameEdit, setUserNameEdit,
    nameInput, setNameInput,
    region, setRegion,
    season, setSeason,
    theme, setTheme,
    userGoal, setUserGoal,
    goalTimeline, setGoalTimeline,
    unlockedAchievements,
    showAchievementPopup,
    toast, setToast,
    undoState,
    copied, setCopied,
    confetti,
    onboardingStep, setOnboardingStep,
    
    // Computed
    doneActions,
    totalCO2Saved,
    progressPercent,
    footprint,
    footprintLabel,
    potentialSavings,
    highImpactDone,
    goalReduction,
    goalProgress,
    monthlyReduction,
    rawFootprint,
    seasonalFactor,
    
    // Actions
    toggleAction,
    handleUndo,
    handleCalculatorAnswer,
    resetCalculator,
    toggleTheme,
    exportProgress,
    importProgress,
    showToast,
    triggerConfetti,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

export default AppContext;
